import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  BusinessAction,
  BusinessDetailResponse,
  BusinessNoteDto,
  BusinessOverviewResponse,
  BusinessDocumentAccessDto,
  BusinessDocumentDto,
  BusinessDto,
  DocumentsAwaitingReviewDto,
  BusinessesListParams,
  BusinessesListResponse,
  BusinessTypeDetailResponse,
  BusinessTypesListResponse,
  CapabilityAction,
  FilePurposeType,
  GigCredentialAccessDto,
  GigCredentialDecision,
  GigCredentialDto,
  GigCredentialQueueRowDto,
  RelationshipAction,
  RelationshipType,
  VerificationDecisionAction,
  VerificationType,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/businesses`;
const TYPES = `${API_VERSION_PREFIX}/hq/business-types`;

function toQuery(params: BusinessesListParams): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  if (params.verification_status) {
    search.set("verification_status", params.verification_status);
  }
  if (params.visibility_status) {
    search.set("visibility_status", params.visibility_status);
  }
  if (params.business_type_id) {
    search.set("business_type_id", params.business_type_id);
  }
  if (params.market_id) search.set("market_id", params.market_id);
  if (params.public_id) search.set("public_id", params.public_id);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createBusinessesApi(client: ApiClient) {
  return {
    list(params: BusinessesListParams = {}) {
      return client.get<BusinessesListResponse>(`${BASE}${toQuery(params)}`);
    },

    getMemberships(businessId: string) {
      return client.get<import("./types").BusinessMembershipsResponse>(`${BASE}/${businessId}/memberships`);
    },

    getDetail(businessId: string) {
      return client.get<BusinessDetailResponse>(`${BASE}/${businessId}`);
    },

    getOverview(businessId: string) {
      return client.get<BusinessOverviewResponse>(`${BASE}/${businessId}/overview`);
    },

    addNote(businessId: string, body: { body: string; pinned?: boolean }) {
      return client.post<{ note: BusinessNoteDto }>(
        `${BASE}/${businessId}/notes`,
        body,
        newIdempotencyKey(),
      );
    },

    pinNote(businessId: string, noteId: string, pinned: boolean) {
      return client.post(
        `${BASE}/${businessId}/notes/${noteId}/actions/pin`,
        { pinned },
        newIdempotencyKey(),
      );
    },

    listTypes() {
      return client.get<BusinessTypesListResponse>(TYPES);
    },

    getType(businessTypeId: string) {
      return client.get<BusinessTypeDetailResponse>(`${TYPES}/${businessTypeId}`);
    },

    create(body: {
      business_type_id: string;
      market_id: string;
      legal_name: string;
      display_name: string;
      timezone_id: string;
      description?: string | null;
      owner_user_id?: string;
    }) {
      return client.post<{ business: BusinessDto; meta: BusinessesListResponse["meta"] }>(
        BASE,
        body,
        newIdempotencyKey(),
      );
    },

    updateProfile(
      businessId: string,
      body: {
        legal_name?: string;
        display_name?: string;
        description?: string | null;
        timezone_id?: string;
        visibility_status?: string;
      },
    ) {
      return client.patch<{ business: BusinessDto; meta: BusinessesListResponse["meta"] }>(
        `${BASE}/${businessId}`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAction(
      businessId: string,
      action: BusinessAction,
      body: {
        reason?: string;
        approval_id?: string;
        required_documents?: string[];
      },
    ) {
      return client.post<{ business: BusinessDto; meta: BusinessesListResponse["meta"] }>(
        `${BASE}/${businessId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    createRelationship(
      businessId: string,
      body: {
        user_id: string;
        relationship_type: RelationshipType;
        status?: string;
        authority_scope?: Record<string, unknown>;
      },
    ) {
      return client.post(
        `${BASE}/${businessId}/relationships`,
        body,
        newIdempotencyKey(),
      );
    },

    applyRelationshipAction(
      businessId: string,
      relationshipId: string,
      action: RelationshipAction,
      body: { reason: string },
    ) {
      return client.post(
        `${BASE}/${businessId}/relationships/${relationshipId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    transferOwnership(
      businessId: string,
      body: {
        new_owner_user_id: string;
        reason: string;
        approval_id: string;
      },
    ) {
      return client.post(
        `${BASE}/${businessId}/actions/transfer-ownership`,
        body,
        newIdempotencyKey(),
      );
    },

    createCapability(
      businessId: string,
      body: {
        business_type_capability_id: string;
        configuration?: Record<string, unknown>;
      },
    ) {
      return client.post(
        `${BASE}/${businessId}/capabilities`,
        body,
        newIdempotencyKey(),
      );
    },

    applyCapabilityAction(
      businessId: string,
      capabilityId: string,
      action: CapabilityAction,
      body: { reason: string; configuration?: Record<string, unknown> },
    ) {
      return client.post(
        `${BASE}/${businessId}/capabilities/${capabilityId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    requestVerification(
      businessId: string,
      body: {
        verification_type?: VerificationType;
        expires_at?: string | null;
      } = {},
    ) {
      return client.post(
        `${BASE}/${businessId}/verifications`,
        body,
        newIdempotencyKey(),
      );
    },

    decideVerification(
      businessId: string,
      verificationId: string,
      body: {
        action: VerificationDecisionAction;
        decision_reason: string;
        approval_id?: string;
        is_override?: boolean;
      },
    ) {
      return client.post(
        `${BASE}/${businessId}/verifications/${verificationId}/actions/decide`,
        body,
        newIdempotencyKey(),
      );
    },

    /**
     * Businesses with submitted documents nobody has decided yet, oldest
     * waiting first. Needs `verification:view`; 403 otherwise.
     */
    listDocumentsAwaitingReview() {
      return client.get<{ businesses: DocumentsAwaitingReviewDto[] }>(
        `${API_VERSION_PREFIX}/hq/business-documents/awaiting-review`,
      );
    },

    /** The business's verification documents. Metadata only — never a URL. */
    listDocuments(businessId: string) {
      return client.get<{ documents: BusinessDocumentDto[] }>(
        `${BASE}/${businessId}/documents`,
      );
    },

    /**
     * A two-minute read URL for one document, minted per click and audited
     * server-side. Never cache it, store it, or put it in a route.
     */
    openDocument(businessId: string, documentId: string) {
      return client.post<{ access: BusinessDocumentAccessDto }>(
        `${BASE}/${businessId}/documents/${documentId}/access`,
        {},
      );
    },

    decideDocument(
      businessId: string,
      documentId: string,
      decision: "accept" | "reject",
      reason?: string,
    ) {
      return client.post<{ document: BusinessDocumentDto }>(
        `${BASE}/${businessId}/documents/${documentId}/actions/${decision}`,
        reason ? { reason } : {},
        newIdempotencyKey(),
      );
    },

    attachFile(
      businessId: string,
      body: { file_asset_id: string; purpose_type: FilePurposeType },
    ) {
      return client.post(
        `${BASE}/${businessId}/files/attach`,
        body,
        newIdempotencyKey(),
      );
    },

    /* ── Gig credentials (Biz review 29 Oct 2026 §5.3) ─────────────────── */

    /**
     * The credential review queue: pending only, oldest waiting first. Needs
     * `verification:view`; businesses the reviewer works for are already
     * filtered out server-side.
     */
    listCredentialsAwaitingReview(limit = 200) {
      return client.get<{ credentials: GigCredentialQueueRowDto[] }>(
        `${API_VERSION_PREFIX}/hq/gig-credentials/awaiting-review?limit=${limit}`,
      );
    },

    /** Every credential the gig holds, as a reviewer sees it. Metadata only. */
    listBusinessCredentialsForReview(businessId: string) {
      return client.get<{ credentials: GigCredentialDto[] }>(
        `${BASE}/${businessId}/credentials`,
      );
    },

    /**
     * A two-minute read URL for one credential file, minted per click and
     * audited server-side. Opening it is refused for a reviewer tied to the
     * business. Never cache or store the URL.
     */
    openCredentialForReview(businessId: string, credentialId: string) {
      return client.post<{ access: GigCredentialAccessDto }>(
        `${BASE}/${businessId}/credentials/${credentialId}/access`,
        {},
      );
    },

    /**
     * Approve or reject. A rejection must carry a note — the gig reads it.
     * Needs `verification:manage`, and the reviewer must be independent.
     */
    decideCredential(
      businessId: string,
      credentialId: string,
      decision: GigCredentialDecision,
      note?: string,
    ) {
      return client.post<{ credential: GigCredentialDto }>(
        `${BASE}/${businessId}/credentials/${credentialId}/actions/${decision}`,
        note ? { note } : {},
        newIdempotencyKey(),
      );
    },
  };
}

export type BusinessesApi = ReturnType<typeof createBusinessesApi>;
